import type { inferRouterInputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import type { GameDataResponse, GamesByFiltersResponse } from './trpc'
import { trpcClient } from './trpc'

type RouterInputs = inferRouterInputs<AppRouter>

export const gameQueries = {
  getGameDataById: async (gameId: string) => {
    return await trpcClient.queryRouter.getGameDataById.query({ gameId })
  },

  getGamesByFilters: async (filters: RouterInputs['queryRouter']['getGamesByFilters']) => {
    return await trpcClient.queryRouter.getGamesByFilters.query(filters)
  },

  getFilterOptions: async () => {
    return await trpcClient.queryRouter.getFilterOptions.query()
  },

  getRecentGames: async (page = 1, limit = 10) => {
    return await trpcClient.queryRouter.getRecentGames.query({ page, limit })
  },

  refreshResponsiveModelsCache: async () => {
    return await trpcClient.queryRouter.refreshResponsiveModelsCache.mutate()
  },

  getResponsiveModels: async () => {
    return await trpcClient.queryRouter.getResponsiveModels.query()
  },

  getModelConfig: async () => {
    return await trpcClient.queryRouter.getModelConfig.query()
  },

  getGameModelPools: async () => {
    return await trpcClient.queryRouter.getGameModelPools.query()
  },

  setGameModelPools: async (input: RouterInputs['queryRouter']['setGameModelPools']) => {
    return await trpcClient.queryRouter.setGameModelPools.mutate(input)
  },

  getBlockedKeywords: async () => {
    return await trpcClient.queryRouter.getBlockedKeywords.query()
  },

  addBlockedKeyword: async (keyword: string, field: 'name' | 'email' | 'displayName') => {
    return await trpcClient.queryRouter.addBlockedKeyword.mutate({ keyword, field })
  },

  removeBlockedKeyword: async (keyword: string, field: 'name' | 'email' | 'displayName') => {
    return await trpcClient.queryRouter.removeBlockedKeyword.mutate({ keyword, field })
  },

  topUpCredit: async (input: RouterInputs['queryRouter']['topUpCredit']) => {
    return await trpcClient.queryRouter.topUpCredit.mutate(input)
  },

  getManualTopups: async () => {
    return await trpcClient.queryRouter.getManualTopups.query()
  },

  getModelPricing: async () => {
    return await trpcClient.queryRouter.getModelPricing.query()
  },

  syncModelPricing: async () => {
    return await trpcClient.queryRouter.syncModelPricing.mutate()
  },

  fetchModelCatalogDiff: async () => {
    return await trpcClient.queryRouter.fetchModelCatalogDiff.mutate()
  },

  getLastCatalogDiff: async () => {
    return await trpcClient.queryRouter.getLastCatalogDiff.query()
  },

  probeModels: async (models: string[], timeoutMs?: number) => {
    return await trpcClient.queryRouter.probeModels.mutate({ models, timeoutMs })
  },

  applyModelConfigUpdate: async (input: RouterInputs['queryRouter']['applyModelConfigUpdate']) => {
    return await trpcClient.queryRouter.applyModelConfigUpdate.mutate(input)
  },

  getReconcileStatus: async () => {
    return await trpcClient.queryRouter.getReconcileStatus.query()
  },

  getPaidUsersDashboard: async () => {
    return await trpcClient.queryRouter.getPaidUsersDashboard.query()
  },

  reconcileUser: async (userId: string) => {
    return await trpcClient.queryRouter.reconcileUser.mutate({ userId })
  },
}

export type GameData = GameDataResponse['data']
export type FilterGame = GamesByFiltersResponse['data'][number]
